import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useCopilot } from "@/context/CopilotContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Sparkles, CheckCircle2, SkipForward, Edit3, HelpCircle, RefreshCcw,
  GitBranch, Workflow, Zap, ArrowRight, ArrowLeft, Loader2, PartyPopper,
  Mic, StopCircle,
} from "lucide-react";

const KIND_META = {
  pipeline:   { label: "Pipeline",   Icon: GitBranch, tint: "bg-[var(--peak-blue)] text-white border-[var(--peak-blue)]",   link: "/pipelines" },
  funnel:     { label: "Funnel",     Icon: Workflow,  tint: "bg-[var(--peak-accent)] text-white border-[var(--peak-accent)]",   link: "/funnels" },
  automation: { label: "Automation", Icon: Zap,       tint: "bg-[var(--peak-ink)] text-white border-[var(--peak-ink)]",   link: "/automations" },
};

const STATUS_PILL = {
  pending:  "bg-white text-[var(--peak-ink)] border-[var(--peak-ink)]",
  approved: "bg-emerald-100 text-emerald-900 border-emerald-800",
  skipped:  "bg-neutral-200 text-neutral-700 border-neutral-700",
};

const money = (n) => (n ? `$${Number(n).toLocaleString()}` : "—");
void money; // reserved for future value display; keeps lint quiet if unused

export default function CopilotRecommendationsPage() {
  const { user } = useAuth();
  const {
    session, busy, demo,
    approveRec, skipRec, explainRec, editRec, finishWalkthrough, regenerateRecs,
    narrateStart, narrateNext, narrateStop,
    openCopilot,
  } = useCopilot();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const cardRefs = useRef({});

  // If the query param says demo=1 but the drawer isn't in demo mode yet,
  // open it in demo mode so the session data is scoped correctly.
  useEffect(() => {
    if (params.get("demo") === "1" && !demo && user?.is_superadmin) {
      openCopilot({ demo: true });
    } else if (!demo) {
      openCopilot();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const recommendations = session?.recommendations || [];
  const totalPending = recommendations.filter((r) => r.status === "pending").length;
  const totalApproved = recommendations.filter((r) => r.status === "approved").length;
  const totalSkipped = recommendations.filter((r) => r.status === "skipped").length;
  const allDone = recommendations.length > 0 && totalPending === 0;
  const isGenerating = session?.state === "generating_recs";
  const progress = session?.generation_progress || { completed: 0, expected: 0 };
  const narratedId = session?.narrated_rec_id || null;
  const walkthroughActive = !!narratedId;

  // Auto-scroll the narrated card into view + brief pulse via ref
  useEffect(() => {
    if (!narratedId) return;
    const el = cardRefs.current[narratedId];
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [narratedId]);

  // Group by product_name, preserve encounter order
  const groups = useMemo(() => {
    const map = new Map();
    for (const r of recommendations) {
      const key = r.product_name || "Company-wide";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    }
    return Array.from(map.entries());
  }, [recommendations]);

  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({ name: "", why: "" });

  const startEdit = (r) => {
    setEditingId(r.id);
    setEditDraft({ name: r.name, why: r.why });
  };
  const saveEdit = async (id) => {
    await editRec(id, { name: editDraft.name, why: editDraft.why });
    setEditingId(null);
    toast.success("Updated");
  };

  const handleRegenerate = async () => {
    if (!window.confirm(
      "Regenerate every pending / skipped recommendation? Already-approved items stay live."
    )) return;
    await regenerateRecs();
    toast.success("Fresh recommendations generated");
  };

  const handleFinish = async () => {
    await finishWalkthrough();
    toast.success("Walkthrough finished");
    const q = demo ? "?demo=1" : "";
    void q;
  };

  if (!session) {
    return (
      <div className="p-10 flex items-center gap-3 text-sm text-[var(--peak-muted)]" data-testid="copilot-recs-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading your recommendations…
      </div>
    );
  }

  // Show the streaming banner immediately even before the first card arrives.
  if (recommendations.length === 0 && isGenerating) {
    return (
      <div className="min-h-screen" data-testid="copilot-recs-page">
        <div className="border-b-2 border-[var(--peak-ink)] bg-white px-6 lg:px-10 py-6">
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--peak-accent)]">Copilot Plan · Streaming</div>
          <h1 className="font-heading text-4xl md:text-5xl font-black tracking-tighter mt-1">Drafting Your Plan</h1>
          <p className="text-sm text-[var(--peak-muted)] mt-2 max-w-2xl">
            Peak Copilot is generating one recommendation at a time. Cards will fade in below as they finish.
          </p>
        </div>
        <div className="p-6 lg:p-10">
          <div className="border-2 border-[var(--peak-ink)] bg-white p-6 flex items-center gap-4" data-testid="copilot-recs-generating-banner">
            <div className="w-12 h-12 flex-none bg-[var(--peak-accent)] text-white flex items-center justify-center border-2 border-[var(--peak-ink)]">
              <Loader2 className="w-6 h-6 animate-spin" strokeWidth={2.5} />
            </div>
            <div className="flex-1">
              <div className="font-heading font-black text-xl tracking-tight">Warming up Claude…</div>
              <div className="text-xs text-[var(--peak-muted)] mt-1">
                First card usually lands within 5-8 seconds. Sit tight.
              </div>
              <div className="h-1 bg-[var(--peak-border)] w-full mt-3 max-w-md">
                <div
                  className="h-full bg-[var(--peak-accent)] transition-all"
                  style={{ width: progress.expected ? `${(100 * (progress.completed || 0)) / progress.expected}%` : "5%" }}
                />
              </div>
            </div>
            <div className="font-mono text-sm text-[var(--peak-ink)]" data-testid="copilot-recs-progress-text">
              {progress.completed || 0} / {progress.expected || "…"}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (recommendations.length === 0 && !isGenerating) {
    return (
      <div className="p-10" data-testid="copilot-recs-empty">
        <div className="max-w-lg border-2 border-[var(--peak-ink)] bg-white p-8">
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--peak-accent)]">Peak Copilot</div>
          <div className="font-heading text-3xl font-black tracking-tighter mt-1">No plan yet</div>
          <p className="text-sm text-[var(--peak-muted)] mt-2">
            Open Peak Copilot on the right and let it review your business first. Once you approve
            the business profile, a tailored recommendation set will land here.
          </p>
          <button
            onClick={() => openCopilot()}
            data-testid="copilot-recs-open-drawer-btn"
            className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-[var(--peak-accent)] hover:bg-[var(--peak-ink)] text-white font-heading font-bold uppercase text-xs tracking-wide"
          >
            <Sparkles className="w-4 h-4" /> Talk to Peak Copilot
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="copilot-recs-page">
      {/* Header */}
      <div className="border-b-2 border-[var(--peak-ink)] bg-white">
        <div className="px-6 lg:px-10 pt-6 pb-4">
          <button
            onClick={() => nav(-1)}
            data-testid="copilot-recs-back"
            className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--peak-muted)] hover:text-[var(--peak-accent)] flex items-center gap-1 mb-2"
          >
            <ArrowLeft className="w-3 h-3" /> Back
          </button>
          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div className="min-w-0">
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--peak-accent)] flex items-center gap-2">
                Copilot Plan
                {demo && (
                  <span className="bg-[var(--peak-accent)] text-white px-1.5 py-0.5 text-[9px] tracking-widest" data-testid="recs-demo-badge">
                    DEMO
                  </span>
                )}
              </div>
              <h1 className="font-heading text-4xl md:text-5xl font-black tracking-tighter mt-1">
                Your Tailored Plan
              </h1>
              <p className="text-sm text-[var(--peak-muted)] mt-2 max-w-2xl">
                Peak Copilot generated these for {session?.business_profile?.company_name || "your business"}.
                Approve in any order — the chat on the right will walk you through it. Every approval creates the
                live entity immediately.
              </p>
            </div>
            <div className="flex items-center gap-2">
              {walkthroughActive ? (
                <>
                  <Button
                    onClick={narrateNext}
                    disabled={busy}
                    data-testid="copilot-recs-narrate-next"
                    className="h-10 bg-[var(--peak-accent)] hover:bg-[var(--peak-ink)] text-white font-heading font-bold uppercase text-xs tracking-wide"
                  >
                    Next <ArrowRight className="w-4 h-4 ml-1.5" />
                  </Button>
                  <Button
                    onClick={narrateStop}
                    disabled={busy}
                    data-testid="copilot-recs-narrate-stop"
                    variant="outline"
                    className="h-10 border-2 border-[var(--peak-ink)] font-heading font-bold uppercase text-xs tracking-wide"
                    title="End walkthrough"
                  >
                    <StopCircle className="w-4 h-4 mr-1.5" /> End
                  </Button>
                </>
              ) : (
                <Button
                  onClick={narrateStart}
                  disabled={busy || totalPending === 0}
                  data-testid="copilot-recs-narrate-start"
                  className="h-10 bg-[var(--peak-blue)] hover:bg-[var(--peak-ink)] text-white font-heading font-bold uppercase text-xs tracking-wide"
                  title="Have Peak Copilot walk you through each recommendation"
                >
                  <Mic className="w-4 h-4 mr-1.5" />
                  Walk me through
                </Button>
              )}
              <Button
                onClick={handleRegenerate}
                disabled={busy || isGenerating}
                data-testid="copilot-recs-regenerate"
                variant="outline"
                className="h-10 border-2 border-[var(--peak-ink)] font-heading font-bold uppercase text-xs tracking-wide"
              >
                <RefreshCcw className="w-4 h-4 mr-1.5" />
                Regenerate
              </Button>
              <Button
                onClick={handleFinish}
                disabled={busy}
                data-testid="copilot-recs-finish"
                className="h-10 bg-[var(--peak-ink)] hover:bg-[var(--peak-accent)] text-white font-heading font-bold uppercase text-xs tracking-wide"
              >
                Finish walkthrough
                <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </div>
          </div>
        </div>
        {/* Stats strip */}
        <div className="px-6 lg:px-10 grid grid-cols-4 border-t-2 border-[var(--peak-ink)]">
          <Stat label="Total" value={recommendations.length} testid="stat-total" first />
          <Stat label="Approved" value={totalApproved} testid="stat-approved" />
          <Stat label="Skipped" value={totalSkipped} testid="stat-skipped" />
          <Stat label="Pending" value={totalPending} testid="stat-pending" last />
        </div>
      </div>

      {isGenerating && (
        <div className="mx-6 lg:mx-10 mt-6 border-2 border-[var(--peak-ink)] bg-white p-4" data-testid="copilot-recs-generating-banner">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex-none bg-[var(--peak-accent)] text-white flex items-center justify-center border-2 border-[var(--peak-ink)]">
              <Loader2 className="w-5 h-5 animate-spin" strokeWidth={2.5} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--peak-accent)]">Peak Copilot · Streaming</div>
              <div className="font-heading font-black text-lg tracking-tight">Drafting your plan…</div>
              <div className="text-xs text-[var(--peak-muted)] mt-0.5">
                Recommendations appear here as Claude finishes each one. Feel free to start approving early.
              </div>
            </div>
            <div className="font-mono text-sm text-[var(--peak-ink)]" data-testid="copilot-recs-progress-text">
              {progress.completed || 0} / {progress.expected || "…"}
            </div>
          </div>
          <div className="h-1 bg-[var(--peak-border)] w-full mt-3">
            <div
              className="h-full bg-[var(--peak-accent)] transition-all"
              style={{ width: progress.expected ? `${(100 * (progress.completed || 0)) / progress.expected}%` : "5%" }}
            />
          </div>
        </div>
      )}

      {allDone && (
        <div className="mx-6 lg:mx-10 mt-6 border-2 border-emerald-800 bg-emerald-50 p-4 flex items-start gap-3" data-testid="copilot-recs-all-done">
          <div className="w-10 h-10 flex-none bg-emerald-600 text-white flex items-center justify-center">
            <PartyPopper className="w-5 h-5" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-heading font-black text-lg tracking-tight">Every recommendation reviewed</div>
            <div className="text-sm text-emerald-900 mt-1">
              Approved {totalApproved} · Skipped {totalSkipped}. Hit <b>Finish walkthrough</b> up top to wrap up, or
              <b> Regenerate</b> to iterate on the plan.
            </div>
          </div>
        </div>
      )}

      {/* Body — grouped by product */}
      <div className="p-6 lg:p-10 space-y-10">
        {groups.map(([productName, items]) => (
          <section key={productName} data-testid={`recs-group-${productName.replace(/\s+/g, "-").toLowerCase()}`}>
            <div className="mb-4">
              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[var(--peak-muted)]">Product / Service</div>
              <h2 className="font-heading text-2xl font-black tracking-tighter mt-1">{productName}</h2>
              <div className="text-xs text-[var(--peak-muted)] mt-1">
                {items.length} recommendation{items.length === 1 ? "" : "s"}
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {items.map((r) => (
                <RecCard
                  key={r.id}
                  rec={r}
                  busy={busy}
                  isEditing={editingId === r.id}
                  isNarrated={narratedId === r.id}
                  cardRef={(el) => { if (el) cardRefs.current[r.id] = el; }}
                  editDraft={editDraft}
                  setEditDraft={setEditDraft}
                  onApprove={() => approveRec(r.id)}
                  onSkip={() => skipRec(r.id)}
                  onExplain={() => explainRec(r.id)}
                  onStartEdit={() => startEdit(r)}
                  onSaveEdit={() => saveEdit(r.id)}
                  onCancelEdit={() => setEditingId(null)}
                />
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

function Stat({ label, value, testid, first, last }) {
  return (
    <div
      className={`py-4 px-4 lg:px-6 ${!last ? "border-r-2 border-[var(--peak-ink)]" : ""} ${first ? "" : ""}`}
      data-testid={testid}
    >
      <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--peak-muted)]">{label}</div>
      <div className="font-heading text-3xl font-black tracking-tighter mt-1">{value}</div>
    </div>
  );
}

function RecCard({ rec, busy, isEditing, isNarrated, cardRef, editDraft, setEditDraft, onApprove, onSkip, onExplain, onStartEdit, onSaveEdit, onCancelEdit }) {
  const meta = KIND_META[rec.kind] || KIND_META.pipeline;
  const Icon = meta.Icon;
  const statusClass = STATUS_PILL[rec.status] || STATUS_PILL.pending;
  return (
    <article
      ref={cardRef}
      className={`border-2 border-[var(--peak-ink)] bg-white flex flex-col transition-shadow ${rec.status === "skipped" ? "opacity-70" : ""} ${isNarrated ? "shadow-[6px_6px_0_0_var(--peak-accent)] ring-2 ring-[var(--peak-accent)] ring-offset-2 ring-offset-[var(--peak-bg)]" : ""}`}
      data-testid={`rec-card-${rec.id}`}
      data-narrated={isNarrated ? "1" : "0"}
    >
      {/* Card header */}
      <header className="p-4 border-b-2 border-[var(--peak-ink)] flex items-center gap-2 flex-wrap">
        <span className={`inline-flex items-center gap-1.5 px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider border-2 ${meta.tint}`}>
          <Icon className="w-3 h-3" strokeWidth={2.5} />
          {meta.label}
        </span>
        <span className={`inline-block px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider border-2 ${statusClass}`} data-testid={`rec-status-${rec.id}`}>
          {rec.status}
        </span>
        {rec.status === "approved" && rec.created_entity_id && (
          <Link
            to={meta.link}
            data-testid={`rec-open-live-${rec.id}`}
            className="ml-auto text-[10px] font-mono uppercase tracking-[0.15em] text-[var(--peak-muted)] hover:text-[var(--peak-accent)] flex items-center gap-1"
          >
            Open live <ArrowRight className="w-3 h-3" />
          </Link>
        )}
      </header>

      {/* Card body */}
      <div className="p-4 flex-1">
        {isEditing ? (
          <>
            <Input
              value={editDraft.name}
              onChange={(e) => setEditDraft((v) => ({ ...v, name: e.target.value }))}
              data-testid={`rec-edit-name-${rec.id}`}
              className="h-9 border-2 border-[var(--peak-ink)] font-heading font-bold text-base mb-2"
            />
            <Textarea
              value={editDraft.why}
              onChange={(e) => setEditDraft((v) => ({ ...v, why: e.target.value }))}
              rows={3}
              data-testid={`rec-edit-why-${rec.id}`}
              className="border-2 border-[var(--peak-ink)] text-xs"
            />
          </>
        ) : (
          <>
            <h3 className="font-heading text-base font-black tracking-tight leading-tight" data-testid={`rec-name-${rec.id}`}>
              {rec.name}
            </h3>
            {rec.why && (
              <p className="text-xs text-[var(--peak-ink)]/75 leading-relaxed mt-2" data-testid={`rec-why-${rec.id}`}>
                {rec.why}
              </p>
            )}
          </>
        )}

        <div className="mt-3">
          <ConfigPreview rec={rec} />
        </div>
      </div>

      {/* Card footer / actions */}
      <footer className="p-3 border-t border-[var(--peak-border)] bg-[var(--peak-bg)] flex flex-wrap items-center gap-2">
        {rec.status === "pending" && !isEditing && (
          <>
            <Button
              onClick={onApprove}
              disabled={busy}
              data-testid={`rec-approve-${rec.id}`}
              className="flex-1 h-9 bg-[var(--peak-accent)] hover:bg-[var(--peak-ink)] text-white font-heading font-bold uppercase text-xs tracking-wide"
            >
              <CheckCircle2 className="w-4 h-4 mr-1.5" />
              Approve & Create
            </Button>
            <Button
              onClick={onSkip}
              disabled={busy}
              data-testid={`rec-skip-${rec.id}`}
              variant="outline"
              className="h-9 border-2 border-[var(--peak-ink)] font-heading font-bold uppercase text-xs tracking-wide"
              title="Skip"
            >
              <SkipForward className="w-3.5 h-3.5" />
            </Button>
          </>
        )}
        {rec.status !== "pending" && !isEditing && (
          <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-[var(--peak-muted)]" data-testid={`rec-status-label-${rec.id}`}>
            {rec.status === "approved" ? "✅ Live in your CRM" : "Skipped"}
          </div>
        )}
        {isEditing ? (
          <>
            <Button
              onClick={onSaveEdit}
              disabled={busy}
              data-testid={`rec-save-${rec.id}`}
              className="flex-1 h-9 bg-[var(--peak-ink)] hover:bg-[var(--peak-accent)] text-white font-heading font-bold uppercase text-xs tracking-wide"
            >
              Save
            </Button>
            <Button
              onClick={onCancelEdit}
              disabled={busy}
              data-testid={`rec-cancel-edit-${rec.id}`}
              variant="outline"
              className="h-9 border-2 border-[var(--peak-ink)] font-heading font-bold uppercase text-xs tracking-wide"
            >
              Cancel
            </Button>
          </>
        ) : (
          <div className="w-full flex items-center gap-3">
            <button
              onClick={onExplain}
              disabled={busy}
              data-testid={`rec-explain-${rec.id}`}
              className="text-[10px] font-mono uppercase tracking-[0.15em] text-[var(--peak-muted)] hover:text-[var(--peak-ink)] flex items-center gap-1"
            >
              <HelpCircle className="w-3 h-3" /> Explain
            </button>
            {rec.status === "pending" && (
              <button
                onClick={onStartEdit}
                disabled={busy}
                data-testid={`rec-edit-${rec.id}`}
                className="text-[10px] font-mono uppercase tracking-[0.15em] text-[var(--peak-muted)] hover:text-[var(--peak-ink)] flex items-center gap-1"
              >
                <Edit3 className="w-3 h-3" /> Edit
              </button>
            )}
          </div>
        )}
      </footer>
    </article>
  );
}

function ConfigPreview({ rec }) {
  const cfg = rec.config || {};
  if (rec.kind === "pipeline") {
    const stages = cfg.stages || [];
    return (
      <div data-testid={`rec-config-pipeline-${rec.id}`}>
        <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-[var(--peak-muted)] mb-1.5">
          {stages.length} stages
        </div>
        <ol className="border-2 border-[var(--peak-ink)] bg-white divide-y divide-[var(--peak-border)]">
          {stages.map((s, i) => (
            <li key={i} className="px-2 py-1.5 flex items-center gap-2 text-xs">
              <span className="w-5 h-5 flex-none bg-[var(--peak-ink)] text-white flex items-center justify-center font-mono text-[10px]">
                {i + 1}
              </span>
              <span className="truncate">{s}</span>
            </li>
          ))}
        </ol>
      </div>
    );
  }
  if (rec.kind === "funnel") {
    return (
      <div data-testid={`rec-config-funnel-${rec.id}`}>
        <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-[var(--peak-muted)] mb-1.5">
          Landing page preview
        </div>
        <div className="border-2 border-[var(--peak-ink)] bg-white p-3 space-y-2">
          <div className="font-heading font-black text-sm leading-tight tracking-tight">
            {cfg.headline || "Headline"}
          </div>
          {cfg.subheadline && <div className="text-[11px] text-[var(--peak-muted)]">{cfg.subheadline}</div>}
          {cfg.body_text && <div className="text-[11px] text-[var(--peak-ink)]/70 line-clamp-3">{cfg.body_text}</div>}
          <div className="pt-2 mt-2 border-t border-[var(--peak-border)] flex items-center justify-between text-[10px] font-mono uppercase tracking-wider text-[var(--peak-muted)]">
            <span>{cfg.form_title || "Contact form"}</span>
            <span className="px-2 py-0.5 bg-[var(--peak-accent)] text-white">{cfg.form_cta || "Send"}</span>
          </div>
        </div>
      </div>
    );
  }
  if (rec.kind === "automation") {
    const actions = cfg.actions || [];
    return (
      <div data-testid={`rec-config-automation-${rec.id}`}>
        <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-[var(--peak-muted)] mb-1.5">
          Trigger + {actions.length} action{actions.length === 1 ? "" : "s"}
        </div>
        <div className="border-2 border-[var(--peak-ink)] bg-white divide-y divide-[var(--peak-border)]">
          <div className="px-2 py-1.5 flex items-center gap-2 text-xs bg-[var(--peak-bg)]">
            <span className="text-[10px] font-mono uppercase tracking-wider text-[var(--peak-accent)]">Trigger</span>
            <span className="font-mono truncate">{cfg.trigger_type || "contact_created"}</span>
          </div>
          {actions.slice(0, 4).map((a, i) => (
            <div key={i} className="px-2 py-1.5 flex items-center gap-2 text-xs">
              <span className="w-5 h-5 flex-none bg-[var(--peak-ink)] text-white flex items-center justify-center font-mono text-[10px]">
                {i + 1}
              </span>
              <span className="font-mono uppercase text-[10px] tracking-wider text-[var(--peak-muted)]">{a.type}</span>
              {a.wait_before_minutes ? (
                <span className="text-[10px] font-mono text-[var(--peak-muted)]">wait {a.wait_before_minutes}m</span>
              ) : null}
            </div>
          ))}
          {actions.length > 4 && (
            <div className="px-2 py-1.5 text-[10px] font-mono text-[var(--peak-muted)]">+{actions.length - 4} more…</div>
          )}
        </div>
      </div>
    );
  }
  return null;
}
